import { ReactElement, useCallback } from "react";
import { DateSlider, SliderWithToggle } from "client/Components";
import { IntegrationTileCalendarViewKind } from "client/Api/Workspace/workspace";
import { TimeZone } from "client/DateAndTime/timezones";
import { getStartOfDay } from "client/DateAndTime/dateTimeUtils";
import { LiveTileContent } from "../../LiveTileContent";
import { PlanningItemModel, PlanningLiveInformationState } from "./PlanningLiveTileContent";
import { PlanningScheduleContent } from "./PlanningScheduleContent";
import { PlanningListItem } from "./PlanningListItem";
import styles from "./PlanningDaySchedule.module.less";

export type PlanningDayScheduleProps = {
	state: PlanningLiveInformationState;
	onReload: () => void;
	appName: string;
	date: Date;
	onDateChange: (date: Date) => void;
	isTodayButtonAvailable: boolean;
	timezone: TimeZone;
	isViewSwitchAvailable: boolean;
	onViewChange: (view: IntegrationTileCalendarViewKind) => void;
};

export const PlanningDaySchedule = (props: PlanningDayScheduleProps): ReactElement => {
	const {
		state,
		onReload,
		appName,
		date,
		onDateChange,
		isTodayButtonAvailable,
		timezone,
		isViewSwitchAvailable,
		onViewChange
	} = props;

	const toggleClickHandler = useCallback(
		(): void => {
			onViewChange(IntegrationTileCalendarViewKind.Week);
		},
		[onViewChange]
	);

	const day: Date = getStartOfDay(date, timezone);

	return (
		<LiveTileContent.ContentStack>
			<div className={styles.sliderContainer}>
				<SliderWithToggle>
					<SliderWithToggle.Slider>
						<DateSlider
							value={day}
							onChange={onDateChange}
							isTodayButtonAvailable={isTodayButtonAvailable}
							trackingId="planning-day-slider"
							timezone={timezone}
						/>
					</SliderWithToggle.Slider>
					{isViewSwitchAvailable && (
						<SliderWithToggle.Toggle
							icon="calendar-week"
							onClick={toggleClickHandler}
						/>
					)}
				</SliderWithToggle>
			</div>

			<LiveTileContent.BusyIndicatorContainer>
				<PlanningScheduleContent
					state={state}
					appName={appName}
					onReload={onReload}
					renderItems={(items: readonly PlanningItemModel[]): ReactElement => (
						<LiveTileContent.Scrollable>
							<LiveTileContent.List>
								{items.map(x => (
									<li key={x.uniqueId}>
										<PlanningListItem
											item={x}
											appName={appName}
											timezone={timezone}
										/>
									</li>
								))}
							</LiveTileContent.List>
						</LiveTileContent.Scrollable>
					)}
				/>
			</LiveTileContent.BusyIndicatorContainer>
		</LiveTileContent.ContentStack>
	);
};
PlanningDaySchedule.displayName = "PlanningDaySchedule";
